import { Injectable } from "@angular/core";
import { DefaultUrlSerializer, NavigationEnd, Router } from "@angular/router";
import { Observable, ReplaySubject } from "rxjs";
import { filter, switchMap, take } from "rxjs/operators";
import { OrderService, PlayerService, PriceService, StorageService } from ".";
import { trackEvent, trackFlow } from './tracking/tealium.utils';
import { TrackingRepository } from "../infrastructure/repositories/tracking.repository";
import { MembershipState } from '../model/enums/membership-state';
import { StorageKeys } from "../model/enums/storage-keys";
import { TrackingEvent } from "../model/enums/tracking-event";
import { Order } from "../model/interfaces/order";
import { OrderUpdateAction } from "../model/interfaces/order-update-action";
import { NetaxeptService } from "./netaxept.service";
import { PaymentService } from "./payment-service";

@Injectable({ providedIn: 'root' })
export class TrackingService
{
    public order: Order;
    private readonly trackingRepository: TrackingRepository;
    private readonly netaxeptService: NetaxeptService;
    private readonly orderService: OrderService;
    private readonly paymentService: PaymentService;
    private readonly playerService: PlayerService;
    private readonly storageService: StorageService;
    private readonly priceService: PriceService;
    private readonly router: Router;
    private readonly urlSerializer = new DefaultUrlSerializer();
    private trackableOrderId: number;
    private trackableOrderIdSubject: ReplaySubject<number>;

    public constructor(
        trackingRepository: TrackingRepository,
        netaxeptService: NetaxeptService, 
        orderService: OrderService, 
        paymentService: PaymentService,
        playerService: PlayerService,
        storageService: StorageService,
        priceService: PriceService,
        router: Router) 
    {
        this.trackingRepository = trackingRepository;
        this.netaxeptService = netaxeptService;
        this.orderService = orderService;
        this.paymentService = paymentService;
        this.playerService = playerService;
        this.storageService = storageService;
        this.priceService = priceService;
        this.router = router;

        this.initRouteTracking();
    }

    private track(event: TrackingEvent): Observable<void>
    {
      return this.getTrackableOrderId().pipe(switchMap(orderId => {
          return this.trackingRepository.track(orderId, event);
      })).pipe(take(1));
    }

    private getTrackableOrderId(): Observable<number>
    {
        if(this.trackableOrderIdSubject != null)
        {
            return this.trackableOrderIdSubject;
        }

        this.trackableOrderIdSubject = new ReplaySubject<number>(1);
        const trackableOrderId = parseInt(this.storageService.getItem(StorageKeys.TrackableOrderId), 10);
        
        if(trackableOrderId > 0)
        {
            this.trackableOrderIdSubject.next(trackableOrderId);
        } 
        else 
        {
          // Not ideal, but given the existing order service and tracking implementation,
          // this approach is the most practical solution for now.
          this.orderService.orderIdSubject
            .pipe(take(1))
            .subscribe(orderId => this.trackableOrderIdSubject.next(orderId));
        }

        return this.trackableOrderIdSubject;        
    }

    private async initRouteTracking()
    {
        const urlTrackingEvents = new Map<string, TrackingEvent>()
            .set('glade-vindere', TrackingEvent.WinnerStoriesVisited)
            .set('om-klasselotteriet', TrackingEvent.AboutVisited)
            .set('login', TrackingEvent.LoginVisited)
            .set('lodder-til-salg', TrackingEvent.BuyPackagesVisited)
            .set('lodder-til-salg/lodder', TrackingEvent.BuyTicketsVisited)
            .set('lodder-til-salg/lodpakker', TrackingEvent.BuyPackagesVisited)
            .set('lodder-til-salg/lodpakker', TrackingEvent.BuyPackagesVisited)
            .set('lodder-til-salg/oenskelodder', TrackingEvent.FindDreamTicketsVisited)
            .set('aktuelle-konkurrencer', TrackingEvent.CompetitionsVisited)
            .set('har-jeg-vundet', TrackingEvent.WinTicketsVisited)
            .set('lodder-til-salg/reservation', TrackingEvent.UpsaleVisited)
            .set('betaling/om-dig', TrackingEvent.CheckoutStarted)

        /// Page view tracking
        this.router.events.pipe(
            filter((event: any) => event instanceof NavigationEnd))
            .subscribe((event =>
            {
                const serializedUrl = this.urlSerializer.parse(event.url);
                const url = serializedUrl.root.children.primary != null ? serializedUrl.root.children.primary.segments.map(s => s.path).join('/') : '';
                const ev = urlTrackingEvents.get(url);

                if (ev != null)
                {
                    this.track(ev).subscribe();
                }
            }));

        // Add to basket
        this.orderService.onOrderContentUpdated.subscribe(update => {
            const updateAction: OrderUpdateAction = update.action;
            if (updateAction.actionType === 'add')
            {
                this.track(TrackingEvent.AddToBasket).subscribe();
            }
        });

        /// Logged in
        this.playerService.onPlayerLoggedIn.subscribe(() => {
            this.orderService.getOrder(false).then(
                order => {
                    this.track(TrackingEvent.LoggedIn).subscribe();
                }
            );
        });

        /// Checkout completed (BS)
        this.paymentService.onPaymentComplete.subscribe(order => {
            this.track(TrackingEvent.CheckoutCompleted).subscribe();
        });

        /// Checkout completed (Card payment)
        this.netaxeptService.onNetaxeptPaymentCompleted.subscribe(order => {
            this.track(TrackingEvent.FMTAdded).subscribe();
        });
    }

    ///////////////////////////////////////////////////
    ////////////// BILLWERK INTEGRATION ///////////////
    ///////////////////////////////////////////////////
  
  private pushToDataLayer(data: any): void {
    if (window['dataLayer'] != null) {
      window['dataLayer'].push(data);
    }
  }

  // Oprettelse af kunden i Oracle ved indtastning af personlige oplysninger
  public playerSignup(order: Order): void {
    // Ensighten
    this.pushToDataLayer({
      event: 'checkout',
      ecommerce: {
        checkout: {
          actionField: {
            step: 'oprettelse',
            customerType: MembershipState.New
          },
          products: null
        }
      }
    });
    
    // Tealium trackEvent
    trackEvent({ 
      name: 'checkout_signup', 
      type: 'link',
    });
  }

  public paymentMethodSelection(order: Order, membershipState: MembershipState): void {
    // Ensighten
    this.pushToDataLayer({
      event: 'checkout',
      ecommerce: {
        checkout: {
          actionField: {
            step: 'betalingsmetode',
            customerType: membershipState,
          },
          products: null
        }
      }
    });
    
    // Tealium trackFlow
    trackFlow({ 
      type: 'link', 
      flowInfo: {
        userFlow: 'Klasselotteriet', 
        flowStep: '1', 
        flowStepName: 'payment_method', 
        flowType: 'normal',
      },
    });
  }

  public paymentServiceSubscriptionSelection(order: Order, membershipState: MembershipState): void {
    // Ensighten
    this.pushToDataLayer({
      event: 'checkout',
      ecommerce: {
        checkout: {
          actionField: {
            step: 'bs',
            customerType: membershipState,
          },
          products: null
        }
      }
    });
    
    // Tealium trackFlow
    trackFlow({ 
      type: 'link', 
      flowInfo: {
        userFlow: 'Klasselotteriet', 
        flowStep: '2', 
        flowStepName: 'confirm_selection', 
        flowType: 'normal',
      },
    });
  }

  public paymentServiceCreateSubscription(order: Order, membershipState: MembershipState): void {
    // Ensighten
    this.pushToDataLayer({
      event: 'checkout',
      ecommerce: {
        checkout: {
          actionField: {
            step: 'bs_oplysninger',
            customerType: membershipState,
          },
          products: null
        }
      }
    });
    
    // Tealium trackFlow
    trackFlow({ 
      type: 'link', 
      flowInfo: {
        userFlow: 'Klasselotteriet', 
        flowStep: '2', 
        flowStepName: 'bs_oplysninger', 
        flowType: 'normal',
      },
    });
  }

  public gapfundAccept(): void {
    // Ensighten
    this.pushToDataLayer({
      event: 'gaEvent',
      eventSyntax: {
        category: 'klasselotteriet',
        action: 'cta_click_spil_med',
        label: 'gapfunding',
      }
    });
    
    // Tealium trackEvent
    trackEvent({ 
      name: 'cta_click', 
      type: 'link', 
      text: 'spil_med', 
      action: 'click',
    });
  }

    private productConsolidation(): Observable<void>
    {
      const orderid: number = this.storageService.getItem("shop.order.id");
      const order = this.orderService.getOrderById(orderid);
      let orderProducts;
      order.pipe(take(1)).subscribe((item) => {
        orderProducts = [
            {
              name: item.Instances[0].Share.Name,
              id: item.Instances[0].Share.Name,
              price: this.priceService.getPriceByOrder(item),
              brand: 'dlo_ddk',
              category: item.TicketPackageInstances[0] !== null ? "lodpakke" : "lod",
              quantity: item.TicketPackageInstances[0] !== null ? item.TicketPackageInstances.length : item.Instances.length,
            },
          ];
      });
      console.log('orderProducts: ', orderProducts);
      return orderProducts;
    } 

    
}
